import React, { useEffect, useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Checkbox,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material'
import { 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon
} from '@mui/icons-material'
import PhotoComparisonDialog from './PhotoComparisonDialog'
import { useMediaUploadViewer } from 'hooks/admin/useMediaUploadViewer'
import { useAccessCodes } from 'hooks/useAccessCodes'
import { useSnackbar } from 'hooks/common/useSnackbar'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { StatusChip } from 'components/StatusChip'
import type { StatusTone } from 'core/theme.tokens'

const mediaProgressTone = (percentage: number): StatusTone => {
  if (percentage === 100) return 'success'
  if (percentage > 0) return 'warning'
  return 'neutral'
}
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'

interface ViewPhotoSubmissionsDialogProps {
  open: boolean
  onClose: () => void
  photoUploadId: string | null
}

interface PhotoPosition {
  id: number
  label: string
}

interface SelectedPhotos {
  [vehicleId: string]: {
    [photoId: string]: boolean
  }
}

const PHOTO_POSITIONS: PhotoPosition[] = [
  { id: 1, label: "FRONT DRIVER-SIDE CORNER" },
  { id: 2, label: "DRIVER SIDE FULL" },
  { id: 3, label: "REAR DRIVER-SIDE CORNER" },
  { id: 4, label: "BACK FULL" },
  { id: 5, label: "REAR PASSENGER-SIDE CORNER" },
  { id: 6, label: "PASSENGER SIDE FULL" },
  { id: 7, label: "FRONT PASSENGER-SIDE CORNER" },
  { id: 8, label: "FRONT FULL" },
  { id: 9, label: "DASH" },
  { id: 10, label: "LEFT SIDE ENGINE" },
  { id: 11, label: "RIGHT SIDE ENGINE" },
  { id: 12, label: "DRIVER SIDE INT" },
  { id: 13, label: "PASSENGER SIDE INT" },
  { id: 14, label: "VIN PLATE" }
]

const ViewPhotoSubmissionsDialog = ({ 
  open, 
  onClose, 
  photoUploadId
}: ViewPhotoSubmissionsDialogProps) => {
  const isMobile = useIsMobile()
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | 'all'>('all')
  const [selectedAccessCode, setSelectedAccessCode] = useState<string | 'all'>('all')
  const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhotos>({})
  const [comparisonState, setComparisonState] = useState<{
    isOpen: boolean
    photoIndex: number
  }>({ isOpen: false, photoIndex: 0 })
  const [isDownloading, setIsDownloading] = useState(false)

  // Use new media upload viewer (photos + videos in parallel)
  const {
    photoUploadDetails,
    photos,
    videos,
    vehiclesWithMedia,
    isLoading,
    isLoadingPhotos,
    isLoadingVideos,
    error,
  } = useMediaUploadViewer(photoUploadId)

  // Get access codes for this photo upload
  const { accessCodes } = useAccessCodes()
  const userAccessCodes = accessCodes.filter(code => 
    code.photoUploadId === photoUploadId
  )

  // Use snackbar for notifications
  const { showSuccess, showError } = useSnackbar()

  // Reset filters when dialog opens/closes or photoUploadId changes
  useEffect(() => {
    setSelectedVehicleId('all')
    setSelectedAccessCode('all')
    setSelectedPhotos({})
  }, [open, photoUploadId])

  // Filter photos based on selected filters
  const displayedPhotos = useMemo(() => {
    let filtered = photos

    // Filter by vehicle
    if (selectedVehicleId !== 'all') {
      filtered = filtered.filter(photo => photo.vehicleId === selectedVehicleId)
    }

    // Filter by access code (using the 'source' field)
    if (selectedAccessCode !== 'all') {
      filtered = filtered.filter(photo => photo.source === selectedAccessCode)
    }

    return filtered
  }, [photos, selectedVehicleId, selectedAccessCode])

  // Filter videos based on selected filters
  const displayedVideos = useMemo(() => {
    let filtered = videos
    if (selectedVehicleId !== 'all') {
      filtered = filtered.filter(v => v.vehicleId === selectedVehicleId)
    }
    // Access code filtering uses source; we currently tag source as photoUploadId
    if (selectedAccessCode !== 'all') {
      filtered = filtered.filter(v => v.source === selectedAccessCode)
    }
    return filtered
  }, [videos, selectedVehicleId, selectedAccessCode])

  // Filter vehicles based on current photo/video filters
  const displayedVehicles = useMemo(() => {
    const vehicleIds = new Set([
      ...displayedPhotos.map(p => p.vehicleId),
      ...displayedVideos.map(v => v.vehicleId),
    ])
    return vehiclesWithMedia.filter((vehicle: any) => vehicleIds.has(vehicle.id))
  }, [vehiclesWithMedia, displayedPhotos, displayedVideos])

  if (!photoUploadId) return null

  const getVehicleProgress = (vehicleId: string) => {
    const vehiclePhotos = displayedPhotos.filter(p => p.vehicleId === vehicleId)
    const vehicleVideos = displayedVideos.filter(v => v.vehicleId === vehicleId)
    const totalPhotos = PHOTO_POSITIONS.length
    const totalMedia = totalPhotos + 1 // 14 photos + 1 video
    const completedPhotos = vehiclePhotos.length
    const completedVideos = vehicleVideos.length > 0 ? 1 : 0
    const completedMedia = completedPhotos + completedVideos
    return {
      completedPhotos,
      totalPhotos,
      videosCount: vehicleVideos.length,
      completedMedia,
      totalMedia,
      percentage: Math.round((completedMedia / totalMedia) * 100),
    }
  }

  const handleVehicleFilterChange = (event: SelectChangeEvent) => {
    setSelectedVehicleId(event.target.value as string)
  }

  const handleAccessCodeFilterChange = (event: SelectChangeEvent) => {
    setSelectedAccessCode(event.target.value as string)
  }

  const handlePhotoSelect = (vehicleId: string, photoId: string) => {
    setSelectedPhotos(prev => ({
      ...prev,
      [vehicleId]: {
        ...(prev[vehicleId] || {}),
        [photoId]: !(prev[vehicleId]?.[photoId] || false)
      }
    }))
  }

  const hasSelectedPhotos = () => {
    return Object.values(selectedPhotos).some(vehicle => 
      Object.values(vehicle).some(selected => selected)
    )
  }

  const handlePhotoClick = (photo: any) => {
    const photoIndex = displayedPhotos.findIndex(p => p.id === photo.id && p.vehicleId === photo.vehicleId)
    if (photoIndex !== -1) {
      setComparisonState({ isOpen: true, photoIndex })
    }
  }

  const downloadPhoto = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading photo:', error)
      throw error
    }
  }

  const handleDownloadPhotos = async () => {
    if (!hasSelectedPhotos() || !photoUploadId) return

    setIsDownloading(true)
    
    const selectedPhotosList = []
    for (const [vehicleId, vehiclePhotos] of Object.entries(selectedPhotos)) {
      for (const [photoId, isSelected] of Object.entries(vehiclePhotos)) {
        if (isSelected) {
          const photo = displayedPhotos.find(p => p.id === photoId && p.vehicleId === vehicleId)
          if (photo) {
            selectedPhotosList.push(photo)
          }
        }
      }
    }

    try {
      let successCount = 0
      let errorCount = 0

      for (const photo of selectedPhotosList) {
        try {
          const vehicle = vehiclesWithMedia.find(v => v.id === photo.vehicleId)
          const vehicleName = vehicle ? `${vehicle.year}_${vehicle.make}_${vehicle.model}` : photo.vehicleId
          const position = photo.angle.toLowerCase().replace(/\s+/g, '_')
          const filename = `${vehicleName}_${position}.jpg`
          await downloadPhoto(photo.url, filename)
          successCount++
        } catch (error) {
          console.error(`Error downloading photo ${photo.id}:`, error)
          errorCount++
        }
      }

      if (successCount > 0) {
        showSuccess(`Successfully downloaded ${successCount} photo${successCount === 1 ? '' : 's'}`)
      }
      if (errorCount > 0) {
        showError(`Failed to download ${errorCount} photo${errorCount === 1 ? '' : 's'}`)
      }
      
      // Clear selections after download attempt
      setSelectedPhotos({})
    } catch (error) {
      console.error('Error downloading photos:', error)
      showError('Failed to download photos. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  const renderVehicleOverview = (vehicle: any) => {
    const vehiclePhotos = displayedPhotos.filter(p => p.vehicleId === vehicle.id)
    const vehicleVideos = displayedVideos.filter(v => v.vehicleId === vehicle.id)
    const progress = getVehicleProgress(vehicle.id)
    
    return (
      <Card key={vehicle.id} sx={{ mb: 3 }}>
        <CardContent>
          {/* Vehicle Header */}
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 1.5, mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {vehicle.make} {vehicle.model} {vehicle.year}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                VIN: {vehicle.vin}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <StatusChip
                dense
                tone={mediaProgressTone(progress.percentage)}
                label={`${progress.completedMedia}/${progress.totalMedia} media`}
                icon={<CheckCircleIcon />}
              />
              <StatusChip
                dense
                tone={progress.videosCount > 0 ? 'success' : 'neutral'}
                label={`${progress.videosCount} video${progress.videosCount === 1 ? '' : 's'}`}
              />
              <Typography variant="body2" color="text.secondary">
                {progress.percentage}% complete
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress.percentage} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: progress.percentage === 100 ? 'success.main' : 'warning.main'
                }
              }} 
            />
          </Box>

          {/* Photo Grid */}
          <Grid container spacing={2}>
            {PHOTO_POSITIONS.map((position) => {
              const photo = vehiclePhotos.find(p => p.angle === position.label)
              const isSelected = photo && selectedPhotos[vehicle.id]?.[photo.id]
              
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={position.id}>
                  <Box
                    sx={{
                      height: 140,
                      border: '2px solid',
                      borderColor: photo ? 'success.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: photo ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': photo ? {
                        borderColor: 'primary.main',
                        transform: 'scale(1.02)',
                        boxShadow: 2
                      } : {}
                    }}
                    onClick={() => photo && handlePhotoClick(photo)}
                  >
                    {/* Position Label */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bgcolor: photo ? 'success.main' : 'grey.400',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        zIndex: 2
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {position.label}
                      </Typography>
                    </Box>

                    {/* Photo or Placeholder */}
                    {photo ? (
                      <>
                        <LazyLoadImage
                          src={photo.url}
                          alt={position.label}
                          effect="blur"
                          placeholderSrc="/placeholder.jpg"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            paddingTop: '24px'
                          }}
                        />
                        
                        {/* Selection Checkbox */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            zIndex: 3
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={isSelected || false}
                            onChange={() => handlePhotoSelect(vehicle.id, photo.id)}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0,0,0,0.5)',
                              borderRadius: '4px',
                              '&.Mui-checked': {
                                color: 'primary.main',
                                bgcolor: 'rgba(255,255,255,0.9)'
                              }
                            }}
                          />
                        </Box>

                        {/* Source indicator */}
                        {photo.source && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: 4,
                              zIndex: 3
                            }}
                          >
                            <Chip
                              label={photo.source.substring(0, 8)}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                fontSize: '0.6rem',
                                height: '20px'
                              }}
                            />
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingTop: '24px',
                          color: 'text.secondary'
                        }}
                      >
                        <CancelIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              )
            })}
          </Grid>

          {/* Videos Grid */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Videos {isLoadingVideos && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Typography>
            <Grid container spacing={2}>
              {vehicleVideos.length === 0 ? (
                <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: 100, border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                    color: 'text.secondary'
                  }}>
                    No videos
                  </Box>
                </Grid>
              ) : (
                vehicleVideos.map((v) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={v.id}>
                    <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bgcolor: 'warning.main', color: 'white', px: 1, py: 0.5, zIndex: 2 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {v.angle || 'START TRUCK VIDEO'}
                        </Typography>
                      </Box>
                      <video src={v.url} controls playsInline style={{ width: '100%', height: 200, objectFit: 'cover' }} />
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>
    )
  }

  const overallProgress = (() => {
    const totalPerVehicle = PHOTO_POSITIONS.length + 1
    const vehiclesSet = new Set(displayedVehicles.map(v => v.id))
    const videosByVehicle = new Set(displayedVideos.map(v => v.vehicleId))
    let completed = 0
    vehiclesSet.forEach((vid) => {
      const photosCount = displayedPhotos.filter(p => p.vehicleId === vid).length
      const videoCount = videosByVehicle.has(vid) ? 1 : 0
      completed += photosCount + videoCount
    })
    const total = vehiclesSet.size * totalPerVehicle
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
    return { completed, total, percentage }
  })()

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { height: { xs: '100dvh', sm: '90vh' }, maxHeight: { xs: '100dvh', sm: '90vh' } }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 0 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Photo Submissions - {photoUploadDetails?.user.email || 'Loading...'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <StatusChip
                dense
                tone={mediaProgressTone(overallProgress.percentage)}
                label={`${overallProgress.completed}/${overallProgress.total} total media`}
                icon={<CheckCircleIcon />}
              />
              <StatusChip
                dense
                tone={displayedVideos.length > 0 ? 'success' : 'neutral'}
                label={`${displayedVideos.length} video${displayedVideos.length === 1 ? '' : 's'}`}
              />
              <Typography variant="body2" color="text.secondary">
                {overallProgress.percentage}% Complete across {displayedVehicles.length} vehicle(s)
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogTitle>
      
      <Divider />

      {/* Filters */}
      <Box sx={{ p: 3, pb: 2, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FilterListIcon color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Filter Photos
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Vehicle</InputLabel>
              <Select
                value={selectedVehicleId}
                label="Vehicle"
                onChange={handleVehicleFilterChange}
              >
                <MenuItem value="all">All Vehicles ({vehiclesWithMedia.length})</MenuItem>
                {vehiclesWithMedia.map((vehicle: any) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.make} {vehicle.model} ({vehicle.vin})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Access Code</InputLabel>
              <Select
                value={selectedAccessCode}
                label="Access Code"
                onChange={handleAccessCodeFilterChange}
              >
                <MenuItem value="all">All Access Codes</MenuItem>
                {userAccessCodes.map(code => (
                  <MenuItem key={code.id} value={code.id}>
                    {code.code} (Created: {new Date(code.createdAt).toLocaleDateString()})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Showing {displayedPhotos.length} photos and {displayedVideos.length} videos
              {selectedAccessCode !== 'all' && ` from access code`}
              {selectedVehicleId !== 'all' && ` for selected vehicle`}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider />
      
      <DialogContent sx={{ p: 3, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : displayedVehicles.length > 0 ? (
          <Box>
            {displayedVehicles.map(vehicle => renderVehicleOverview(vehicle))}
          </Box>
        ) : (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="400px"
          >
            <Typography color="text.secondary" variant="h6">
              {selectedAccessCode !== 'all' || selectedVehicleId !== 'all' 
                ? 'No photos found with current filters' 
                : 'No photo submissions found'
              }
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {hasSelectedPhotos() && 
            `${Object.values(selectedPhotos).reduce((acc, vehicle) => 
              acc + Object.values(vehicle).filter(Boolean).length, 0
            )} photos selected`
          }
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {hasSelectedPhotos() && (
            <Button
              onClick={handleDownloadPhotos}
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              disabled={isDownloading}
              sx={{ minWidth: 180 }}
            >
              {isDownloading ? 'Downloading...' : 'Download Photos'}
            </Button>
          )}
          <Button 
            onClick={onClose}
            variant="outlined"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
      <PhotoComparisonDialog
        open={comparisonState.isOpen}
        onClose={() => setComparisonState({ isOpen: false, photoIndex: 0 })}
        photos={displayedPhotos.map(photo => ({
          id: photo.id,
          url: photo.url,
          angle: photo.angle,
          vehicleInfo: photo.vehicleInfo,
          uploadedAt: photo.uploadedAt,
        }))}
        currentPhotoIndex={comparisonState.photoIndex}
        onPhotoChange={(index) => setComparisonState({ isOpen: true, photoIndex: index })}
      />
    </Dialog>
  )
}

export default ViewPhotoSubmissionsDialog